import { Badge, Box, Flex, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { ExclusionGroundsSummary } from "../ExclusionGroundsSummary";
import { EmptyValue, SummaryField } from "./SummaryField";
import { FieldErrorBox } from "./FieldErrorBox";
import { SelectionCriteriaList } from "./SelectionCriteriaList";
import { SummaryGroupHeader } from "./SummaryGroupHeader";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "SelectionCriteriaPresent",
  "SelectionCriteriaUnique",
] as const satisfies readonly KnownFieldKey[];

export interface ExclusionGroundsSummaryData {
  mandatoryCount: number;
  voluntaryActiveCount: number;
  voluntaryTotalCount: number;
  disabledVoluntaryCriteria: { id: string; name: string; nameNorwegian: string | null }[];
}

interface QualificationSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
  exclusionGroundsSummary: ExclusionGroundsSummaryData;
}

export const QualificationSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
  exclusionGroundsSummary,
}: QualificationSectionProps) => {
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language.startsWith("no");
  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );
  const selectionCriteriaUniqueStatus = readinessFor("SelectionCriteriaUnique");

  const selectionCriteria = event.eventData.selectionCriteria ?? [];
  const hasCriteria = selectionCriteria.length > 0;
  const { disabledVoluntaryCriteria, ...counts } = exclusionGroundsSummary;
  const hasDisabled = disabledVoluntaryCriteria.length > 0;

  return (
    <SummarySection
      title={t("Qualification requirements")}
      editTarget={{ kind: "wizard-step", step: "qualification" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      {hasCriteria ? (
        <>
          <SelectionCriteriaList criteria={selectionCriteria} />
          {selectionCriteriaUniqueStatus?.error && (
            <FieldErrorBox error={selectionCriteriaUniqueStatus.error} />
          )}
        </>
      ) : (
        <SummaryField
          label={t("Selection criteria")}
          value={<EmptyValue text={t("Not provided")} />}
          status={readinessFor("SelectionCriteriaPresent")}
          extraStatuses={[selectionCriteriaUniqueStatus]}
        />
      )}
      <Box mt="5">
        <SummaryGroupHeader>
          <Flex as="span" align="center" gap="2">
            {t("Exclusion grounds")}
            <Badge bg="smSuccessSoft" color="smSuccess" fontSize="2xs">
              {t("Complete")}
            </Badge>
          </Flex>
        </SummaryGroupHeader>
        <ExclusionGroundsSummary {...counts} />
        <Box mt="2">
          {hasDisabled ? (
            <>
              <Text fontSize="xs" color="smMuted" mb="1">
                {t("Disabled voluntary grounds")}
              </Text>
              <UnorderedList spacing="1" pl="4">
                {disabledVoluntaryCriteria.map((c) => (
                  <ListItem key={c.id} fontSize="sm">
                    {isNorwegian ? c.nameNorwegian ?? c.name : c.name}
                  </ListItem>
                ))}
              </UnorderedList>
            </>
          ) : (
            <Text fontSize="sm" color="smMuted" fontStyle="italic">
              {t("All voluntary grounds active (DFO recommendation)")}
            </Text>
          )}
        </Box>
      </Box>
    </SummarySection>
  );
};
